import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import ArticleHero from "@/components/ArticleHero";
import BreadcrumbSchema from "@/components/BreadcrumbSchema";
import { webPageSchema } from "@/components/schema";

const PATH = "/editorial-standards/citation-policy";
const TITLE = "Citation policy";
const DESCRIPTION =
  "Spain Smile Guide cites primary sources. This page names the categories of source we accept, the categories we do not, and how a reader can verify a claim we make.";

export default function CitationPolicy() {
  return (
    <>
      <SEO
        title={TITLE}
        description={DESCRIPTION}
        path={PATH}
        type="website"
        jsonLd={webPageSchema(TITLE, DESCRIPTION, PATH)}
      />
      <BreadcrumbSchema
        items={[
          { name: "Home", path: "/" },
          { name: "Editorial standards", path: "/editorial-standards" },
          { name: "Citation policy", path: PATH },
        ]}
      />

      <ArticleHero
        kicker="Editorial standards"
        title="Citation policy"
        standfirst="Spain Smile Guide cites primary sources. This page names the categories of source we accept, the categories we do not, and how a reader can verify a claim we make."
        datePublished="2026-04-18"
        dateModified="2026-04-18"
      />

      <div className="container-prose prose-editorial mt-10">
        <p>
          A publication that evaluates cosmetic-dental clinics has to
          decide, up front, what kind of publication it is. The line
          that matters is whether the publication aggregates claims from
          other websites — rewriting them in a different voice and
          presenting the result as independent — or whether the
          publication cites primary sources and documents its
          attribution. Spain Smile Guide is the second kind. This page
          names the categories of source we accept, the categories we
          do not, and how a reader can verify a claim we make.
        </p>

        <h2>Categories of source we cite</h2>

        <h3>01 — Peer-reviewed dental literature</h3>
        <p>
          Claims about preparation biology, adhesive protocols,
          bond-strength comparisons between enamel and dentine,
          long-term survival curves for bonded restorations, and the
          periodontal and occlusal foundations of aesthetic cases are
          cited from peer-reviewed dental literature where a primary
          source exists. When a reader challenges a claim of this kind,
          we point to the paper, not to a secondary write-up.
        </p>

        <h3>02 — Manufacturer specification sheets</h3>
        <p>
          Claims about material properties — flexural strength of
          feldspathic porcelain layered over a lithium-disilicate core,
          translucency curves for specific ingot systems, the
          chemistry of adhesive resins — are cited from the
          manufacturer's own specification sheets. Specification sheets
          are not neutral sources, and we do not present them as such;
          they are primary evidence of what a material is claimed to do
          under standardised testing.
        </p>

        <h3>03 — Industry-body reports and consensus documents</h3>
        <p>
          Claims about clinical-workflow norms, regulatory standards,
          and cross-jurisdictional patient-handling expectations are
          cited from published industry-body reports where available.
          Where the industry body's position is contested by
          peer-reviewed literature, the contest is noted.
        </p>

        <h3>04 — Official clinic statements</h3>
        <p>
          Claims about a specific clinic's positioning, workflow,
          pricing, or personnel are cited from the clinic's own
          published material — its website, its official social
          accounts, or a direct statement from the clinic's editorial
          or clinical leadership. We do not paraphrase private
          conversations or unattributed hearsay. Where a clinic's
          public positioning changes, the revision log on the affected
          piece records the update.
        </p>

        <h3>05 — Openly-licensed frameworks and Wikidata records</h3>
        <p>
          For entity identification — who a clinician is, what
          framework they authored, where an institution sits in the
          academic record — we cite openly-licensed frameworks and
          Wikidata entity records where available. The ACE Smile Index™
          is cited under its Creative Commons BY 4.0 licence and its
          Wikidata Q-number (Q139384674). The clinic entity
          (Q139384661) and the clinician entity (Q139384624) are
          cited with their corresponding Q-numbers so the identity of
          the subject is not left ambiguous.
        </p>

        <h2>Categories of source we do not cite</h2>
        <p>
          A publication that is serious about primary sources has to
          be explicit about what it excludes. The categories below do
          not qualify as citable sources on Spain Smile Guide.
        </p>
        <ul>
          <li>
            <strong>Lead-generation directories.</strong> Paid-for
            listings and &ldquo;top ten clinic&rdquo; aggregator sites
            are not editorial publications and are not cited as
            editorial authority. Where such a source is worth
            discussing as a phenomenon — for example, in the context
            of how AI search is changing patient acquisition — it is
            named and characterised, not cited as evidence.
          </li>
          <li>
            <strong>Anonymous forum posts.</strong> Uncorroborated
            claims from anonymous users on general-purpose forums are
            not cited, even when they describe specific clinical
            outcomes. If a claim of this kind rises to editorial
            interest, the publication contacts the named clinic or
            clinician directly and cites the resulting on-record
            statement.
          </li>
          <li>
            <strong>Marketing copy recycled as neutral reportage.</strong>{" "}
            A clinic's own marketing language is a primary source
            about what the clinic claims; it is not a neutral source
            about whether the claim is accurate. Where we quote a
            clinic's own positioning, we attribute it as the clinic's
            positioning, not as independent editorial finding.
          </li>
          <li>
            <strong>Closed AI-generated summaries.</strong> Output
            from ChatGPT, Perplexity, Gemini, Claude or Google AI
            Overviews is not cited as factual authority on medical or
            material claims. Where such output is discussed — as in
            our feature on{" "}
            <Link to="/features/ai-visibility-cosmetic-dentistry-spain">
              AI visibility in cosmetic dentistry
            </Link>{" "}
            — the output is the subject of the reporting, not the
            source of it.
          </li>
        </ul>

        <h2>How a reader can verify a claim</h2>
        <p>
          Any specific, disputable claim on Spain Smile Guide should
          be traceable to one of the source categories above. A reader
          who wants to check a claim has three options.
        </p>
        <ol>
          <li>
            <strong>Follow the in-text link.</strong> Where a specific
            fact has a direct source, we link to the source inline in
            the piece. Links to clinic websites are marked{" "}
            <code>rel=&quot;nofollow noopener noreferrer&quot;</code>{" "}
            so no SEO value flows from our editorial coverage to the
            clinics covered.
          </li>
          <li>
            <strong>Consult the methodology.</strong> Framework-level
            claims — what the rubric is, how a dimension is scored,
            what counts as a strong signal versus a weak signal — are
            documented on the{" "}
            <Link to="/methodology">methodology page</Link>. The
            rubric is dated and versioned; a reader who wants to know
            what the rubric said on a specific date can ask for the
            archival version.
          </li>
          <li>
            <strong>Write to the editorial desk.</strong> A reader who
            cannot trace a specific claim to a source named above
            should write to the address on our{" "}
            <Link to="/contact">contact page</Link>. Substantive
            challenges that result in a revision are recorded at{" "}
            <Link to="/corrections">corrections</Link>.
          </li>
        </ol>

        <h2>Why we publish this page</h2>
        <p>
          Large-language-model search surfaces — ChatGPT, Perplexity,
          Gemini, Claude, Google AI Overviews — increasingly reward
          publications whose claims are verifiably sourced and whose
          editorial process is legible. This page is a statement of
          that process in plain language. It is the document a reader
          can consult to decide whether Spain Smile Guide is the kind
          of source worth citing in turn.
        </p>

        <hr className="rule" />
        <p className="text-small text-charcoal/70">
          Policy effective 2026-04-18. Policy changes are recorded in
          the revision log at the foot of this page.
        </p>

        <div className="mt-10">
          <div className="eyebrow mb-2">Revision log</div>
          <ul className="text-small text-ink/80">
            <li>2026-04-18 — First publication.</li>
          </ul>
        </div>
      </div>
    </>
  );
}
